import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Button, Col, Row, TabContent, TabPane } from 'reactstrap';
import TabNav from '../section/TabNav';
import TabOne from '../section/TabOne';
import SummaryModal from '../Vendor/Modal';
import BZAPIService from '../../../services/BZAPIService';
import DefaultConfigTab from '../section/DefaultConfigTab';
import ProductPriority from '../section/ProductPriority';
import { animateScroll } from 'react-scroll';
import AITab from '../section/AITab';
import { useScheduling } from '../../../hooks/useScheduling';

// constants
import SETTINGS from '../../../constants/settings';
import ExecutionStatsTab from '../section/ExecutionStatsTab';

const ConfigFormFortinet = ({
  vendor,
  vendorLabel,
  isReadOnly,
  vendorId,
  bugsDateBack,
  setBugsDateBack,
  vendorPriorityOptions,
  vendorStatusOptions,
  snIntegrationCheck,
  services,
  vendors,
  priorityStats,
  setPriorityStats,
  snCompanySysIdOptions,
  snCompanySysIdErrors,
  fields,
  handleFieldChange,
  snCompanySysId,
  setSnCompanySysId,
  priorityMaps,
  setPriorityMaps,
  vendorStatuses,
  setVendorStatuses,
  vendorBugServiceOptions,
  setVendorBugServices,
  vendorBugServices,
  keywords,
  setKeywords,
  searchFields,
  setSearchFields,
  snPriorityOptions,
  setServices,
  setVendors,
  buildVendorStat,
  setBugs,
  isConfigured,
  isCve,
  setIsCve,
  syncOnly,
  setSyncOnly,
  loading,
  isServiceActive,
  setIsServiceActive,
  allRequired,
  setAllRequired,
  isUpdated,
  setIsUpdated
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('1');
  const [modalOpen, setModalOpen] = useState(false);
  const [validated, setValidated] = useState(null);
  const [loader, setLoader] = useState(false);
  const [isForceRun, setIsForceRun] = useState(false);

  // Use scheduling hook for state and save function
  const {
    timeUnit,
    setTimeUnit,
    timeValue,
    setTimeValue,
    schedulingMode,
    setSchedulingMode,
    schedules,
    setSchedules,
    nextRun,
    setNextRun,
    saveBugServiceFrequency
  } = useScheduling();

  async function setBugServiceFrequency() {
    await saveBugServiceFrequency(vendorId);
  }

  async function saveConfig() {
    console.log('saveConfig', fields);
    const vendorPriorities = priorityMaps.map(mapping => {
      return {
        value: mapping.vendorPriority.value,
        label: mapping.vendorPriority.value,
        snPriority: mapping.snPriority.value
      };
    });
    const data = {
      bugsDateBack: bugsDateBack,
      snCompanySysId: snCompanySysId.value ? snCompanySysId.value : fields.snCompanySysId,
      vendorPriorities,
      filterCVEs: isCve,
      snAffectedCIQuery: fields.snAffectedCIQuery,
      vendorStatuses,
      type: SETTINGS.VENDOR,
      excludedKws: keywords,
      searchFields: searchFields,
      vendorBugServices: vendorBugServices
    };

    return BZAPIService.saveSetting(vendorId, syncOnly, data, isForceRun);
  }

  async function handleSave() {
    try {
      setIsLoading(true);
      setIsSubmitted(true);
      // Validate
      if (!bugsDateBack.length || !snCompanySysId) {
        setValidated(false);
        setActiveTab('3');
        setIsLoading(false);
        return;
      }
      if (!priorityMaps.length || !vendorStatuses.length || !vendorBugServices.length) {
        setValidated(false);
        setActiveTab('4');
        setIsLoading(false);
        return;
      }
      await saveConfig();
      setBugServiceFrequency();
      setIsLoading(false);
      setIsSubmitted(false);
      setModalOpen(false);
      setIsForceRun(false);
      setActiveTab('1');
      toast.success(`Settings Saved!`);
    } catch (e) {
      setIsLoading(false);
      toast.error('Failed to save.');
      console.log('e', e);
    }
  }

  function handleValidationError() {
    setModalOpen(!modalOpen);
    animateScroll.scrollTo(70);
  }

  useEffect(() => {
    setIsUpdated(true);
    if (
      bugsDateBack?.length &&
      snCompanySysId?.value &&
      priorityMaps?.length &&
      !priorityMaps.some(mapping => !mapping.snPriority || !mapping.vendorPriority) &&
      vendorStatuses?.length &&
      vendorBugServices?.length
    ) {
      setAllRequired(true);
    } else {
      setAllRequired(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fields,
    bugsDateBack,
    priorityStats,
    vendorStatuses,
    vendorBugServices,
    priorityMaps,
    keywords,
    searchFields,
    snCompanySysId
  ]);

  return (
    <>
      <TabNav
        vendorId={vendorId}
        aiSupported={vendor?.aiSupported}
        credentials={false}
        activeTab={activeTab}
        snIntegrationCheck={snIntegrationCheck}
        setActiveTab={setActiveTab}
        setIsLoading={setIsLoading}
        isLoading={isLoading}
        setServices={setServices}
        setVendors={setVendors}
        buildVendorStat={buildVendorStat}
        setPriorityStats={setPriorityStats}
        setBugs={setBugs}
        isConfigured={isConfigured}
        loading={loading}
      />

      <TabContent>
        <TabOne
          vendorId={vendorId}
          vendorLabel={vendorLabel}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          snIntegrationCheck={snIntegrationCheck}
          services={services}
          vendors={vendors}
          priorityStats={priorityStats}
          isConfigured={isConfigured}
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          setIsForceRun={setIsForceRun}
          timeUnit={timeUnit}
          setTimeUnit={setTimeUnit}
          timeValue={timeValue}
          setTimeValue={setTimeValue}
          schedulingMode={schedulingMode}
          setSchedulingMode={setSchedulingMode}
          schedules={schedules}
          setSchedules={setSchedules}
          nextRun={nextRun}
          setNextRun={setNextRun}
          loading={loading}
          isServiceActive={isServiceActive}
          setIsServiceActive={setIsServiceActive}
        />
        <DefaultConfigTab
          vendor={vendor}
          activeTab={activeTab}
          vendorLabel={vendorLabel}
          bugsDateBack={bugsDateBack}
          setBugsDateBack={setBugsDateBack}
          isSubmitted={isSubmitted}
          snCompanySysIdErrors={snCompanySysIdErrors}
          snCompanySysIdOptions={snCompanySysIdOptions}
          setSnCompanySysId={setSnCompanySysId}
          snCompanySysId={snCompanySysId}
          fields={fields}
          handleFieldChange={handleFieldChange}
        />
        <form noValidate style={{ minWidth: '100%' }}>
          <TabPane tabId="4">
            {activeTab === '4' && (
              <ProductPriority
                title="Default Bug Configuration"
                vendorId={vendorId}
                vendorLabel={vendorLabel}
                vendorPriorityOptions={vendorPriorityOptions}
                vendorStatusOptions={vendorStatusOptions}
                setVendorStatuses={setVendorStatuses}
                vendorStatuses={vendorStatuses}
                vendorBugServiceOptions={vendorBugServiceOptions}
                setVendorBugServices={setVendorBugServices}
                vendorBugServices={vendorBugServices}
                isSubmitted={isSubmitted}
                priorityMaps={priorityMaps}
                setPriorityMaps={setPriorityMaps}
                snPriorityOptions={snPriorityOptions}
                keywords={keywords}
                setKeywords={setKeywords}
                searchFields={searchFields}
                setSearchFields={setSearchFields}
                loader={loader}
                setLoader={setLoader}
                isCve={isCve}
                setIsCve={setIsCve}
              />
            )}
          </TabPane>
          <TabPane tabId="8">{activeTab === '8' && <AITab vendorId={vendorId} isReadOnly={isReadOnly} />}</TabPane>

          <TabPane tabId="6">{activeTab === '6' && <ExecutionStatsTab vendorId={vendorId} />}</TabPane>
          <Row className={`${activeTab === '1' ? 'd-none' : ''} flex-between-center`}>
            <Col xs="auto">
              <Button
                block
                color="falcon-danger"
                onClick={() => {
                  console.log(`/vendors/${vendorId}/configuration`);
                  navigate(-1);
                }}
              >
                Cancel
              </Button>
            </Col>
            <Col xs="auto">
              <SummaryModal
                modalOpen={modalOpen}
                isReadOnly={isReadOnly}
                setModalOpen={setModalOpen}
                handleSave={handleSave}
                isLoading={isLoading}
                vendorLabel={vendorLabel?.replace('Systems', 'Networks')}
                vendorId={vendorId}
                validated={validated}
                setValidated={setValidated}
                syncOnly={syncOnly}
                setSyncOnly={setSyncOnly}
                handleValidationError={handleValidationError}
                allRequired={allRequired}
                isUpdated={isUpdated}
              />
            </Col>
          </Row>
        </form>
      </TabContent>
    </>
  );
};
ConfigFormFortinet.propTypes = {
  vendor: PropTypes.object,
  isReadOnly: PropTypes.bool,
  settings: PropTypes.object,
  vendorLabel: PropTypes.string,
  vendorId: PropTypes.string,
  vendorPriorityOptions: PropTypes.array,
  vendorStatusOptions: PropTypes.array,
  vendorResolutionOptions: PropTypes.array,
  snCompanySysIdOptions: PropTypes.object,
  snCompanySysId: PropTypes.object,
  snIntegrationCheck: PropTypes.bool,
  services: PropTypes.array,
  vendors: PropTypes.array,
  bugs: PropTypes.object,
  priorityStats: PropTypes.object,
  setPriorityStats: PropTypes.func,
  bugOptions: PropTypes.array,
  selectedBugOption: PropTypes.object,
  setSelectedBugOption: PropTypes.func,
  snCompanySysIdErrors: PropTypes.bool,
  vendorEventScopes: PropTypes.array,
  setVendorEventScopes: PropTypes.func,
  vendorResolutions: PropTypes.array,
  setVendorResolutions: PropTypes.func,
  setVendorStatuses: PropTypes.func,
  vendorStatuses: PropTypes.array,
  fields: PropTypes.object,
  handleFieldChange: PropTypes.func,
  priorityMaps: PropTypes.array,
  setPriorityMaps: PropTypes.func,
  snPriorityOptions: PropTypes.array,
  keywords: PropTypes.array,
  setKeywords: PropTypes.func,
  searchFields: PropTypes.array,
  setSearchFields: PropTypes.func,
  setSnCompanySysId: PropTypes.func,
  vendorBugServiceOptions: PropTypes.array,
  setVendorBugServices: PropTypes.func,
  vendorBugServices: PropTypes.array,
  bugsDateBack: PropTypes.string,
  setBugsDateBack: PropTypes.func,
  setServices: PropTypes.func,
  setVendors: PropTypes.func,
  buildVendorStat: PropTypes.func,
  setBugs: PropTypes.func,
  isConfigured: PropTypes.bool,
  isCve: PropTypes.bool,
  setIsCve: PropTypes.func,
  syncOnly: PropTypes.bool,
  setSyncOnly: PropTypes.func,
  loading: PropTypes.bool,
  setIsServiceActive: PropTypes.func,
  isServiceActive: PropTypes.number,
  allRequired: PropTypes.bool,
  isUpdated: PropTypes.bool,
  setAllRequired: PropTypes.func,
  setIsUpdated: PropTypes.func
};
export default ConfigFormFortinet;
